"use client";

import { sendTelemetry } from "./telemetry-client";
import { getTelemetryContext } from "./telemetry-context";

declare global {
  interface Window {
    __csdeskTelemetryErrorsRegistered?: boolean;
  }
}

function toMessage(reason: any): string {
  if (typeof reason === "string") return reason;
  if (reason?.message) return String(reason.message);
  try {
    return JSON.stringify(reason);
  } catch {
    return "Unhandled rejection";
  }
}

export function registerGlobalErrorTracking() {
  if (typeof window === "undefined") return () => {};
  if (window.__csdeskTelemetryErrorsRegistered) return () => {};

  window.__csdeskTelemetryErrorsRegistered = true;

  const onError = (event: ErrorEvent) => {
    const ctx = getTelemetryContext();
    if (!ctx.pageKey || !ctx.route) return;

    const err = event.error as Error | undefined;

    sendTelemetry({
      schemaVersion: 1,
      type: "error",
      pageKey: ctx.pageKey,
      route: ctx.route,
      sampleRate: ctx.sampleRate,
      error: {
        name: err?.name ?? "Error",
        message: String(err?.message ?? event.message ?? "Unknown error"),
        stack: err?.stack,
        source: event.filename,
      },
    });
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    const ctx = getTelemetryContext();
    if (!ctx.pageKey || !ctx.route) return;

    const reason: any = event.reason;

    sendTelemetry({
      schemaVersion: 1,
      type: "error",
      pageKey: ctx.pageKey,
      route: ctx.route,
      sampleRate: ctx.sampleRate,
      error: {
        name: reason?.name ?? "UnhandledRejection",
        message: toMessage(reason),
        stack: reason?.stack,
      },
    });
  };

  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onUnhandledRejection);

  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
    window.__csdeskTelemetryErrorsRegistered = false;
  };
}
